/**
 * GenerateMappingDialog — Two-step dialog for generating field mappings.
 *
 * Step 1: Select a target schema (reuses listSchemas + card pattern from SchemaSetPickerDialog).
 * Step 2: Choose mapping mode — Mapping Engine or Iterative (AI-Assisted).
 * Schemas uploaded via Target Schemas → Upload Schema → Template tab skip step 2
 * and go straight to the readiness drawer (iterative mapping).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Schema as SchemaIcon,
  ArrowBack as BackIcon,
  Chat as IterativeIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  TableChart as TablesIcon,
} from '@mui/icons-material';
import { listSchemas, resolveTargetTableNames } from '../../services/schemaApi';
import type { Schema } from '../../services/schemaApi';
import type { WorkspaceTable } from '../../services/workspaceApi';
import WorkspaceTablePicker from '../common/WorkspaceTablePicker';
import { iterAllLevels } from '../../lib/outputStructure';

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Return only table names that are data tables (match an output_structure level).
 * When there's no output_structure, returns all tables (everything is mappable).
 */
function getMappableTableNames(
  definition: Record<string, any> | undefined,
  allTableNames: string[],
): string[] {
  if (!definition) return allTableNames;
  const os = definition.output_structure;
  if (!os || !os.levels || !os.levels.length) return allTableNames;

  // Walk the tree — a parent may declare sibling children at any depth,
  // and mappable-table inference must cover every level in the structure.
  const levelNames = new Set<string>();
  for (const lvl of iterAllLevels(os)) {
    const elem: string = lvl.element_name || '';
    if (!elem) continue;
    const normalized = elem.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
    levelNames.add(normalized);
    levelNames.add(elem.toLowerCase());
  }

  const mappable = allTableNames.filter((name) => {
    const tn = name.toLowerCase();
    if (levelNames.has(tn)) return true;
    // Try with/without common suffixes
    for (const suffix of ['_data', '_defs', 's', '_groups']) {
      if (tn.endsWith(suffix) && levelNames.has(tn.slice(0, -suffix.length))) return true;
      if (levelNames.has(tn + suffix)) return true;
    }
    return false;
  });

  return mappable.length > 0 ? mappable : allTableNames;
}

/**
 * Check if a table name is a data table (in the output_structure hierarchy).
 */
function isDataTable(
  tableName: string,
  definition: Record<string, any> | undefined,
): boolean {
  if (!definition?.output_structure?.levels?.length) return true;
  const mappable = getMappableTableNames(definition, [tableName]);
  return mappable.length > 0;
}

/**
 * True when the target schema was uploaded from the Template tab on Schema Sets
 * (Upload Schema → Template → XSD/XML/JSON). Matches ``source_type: "template"``
 * from ``SchemaSetManager`` / ``schema_registry`` — not File Upload, SQL DDL,
 * or Connector paths (those keep the mapping-mode step).
 */
function isTemplateTabUploadedTargetSchema(schema: Schema | null | undefined): boolean {
  if (!schema) return false;

  const markers = [
    schema.source_type,
    (schema.provenance as { upload_source?: string } | undefined)?.upload_source,
    (schema.metadata as { source_format?: string } | undefined)?.source_format,
  ].map((value) => String(value || '').toLowerCase());
  if (markers.some((value) => value === 'template')) {
    return true;
  }

  // Template-tab uploads always embed template_summary (XSD/XML/JSON). Needed when
  // source_type was not persisted on older rows (see schema_registry.create_schema fix).
  const templateSummary = schema.definition?.template_summary;
  return Boolean(templateSummary && typeof templateSummary === 'object');
}

// ── Types ────────────────────────────────────────────────────────────

type MappingMode = 'engine' | 'iterative';

interface GenerateMappingDialogProps {
  open: boolean;
  onClose: () => void;
  batchId: string;
  sessionId: string;
  /** Currently selected schema ID for highlighting. */
  currentSchemaId?: string | null;
  /** Called when user selects Mapping Engine mode. */
  onMappingEngineStart: (schema: Schema, selectedSourceTables?: string[], selectedTargetTables?: string[]) => void;
  /** Called when user selects Iterative (AI-Assisted) mode. */
  onIterativeStart: (schema: Schema, selectedSourceTables?: string[], selectedTargetTables?: string[]) => void;
  /**
   * Called after the user finishes the Mode step. Hands the picked
   * schema, mode, and any table subset to the host so the host can
   * open the persistent readiness drawer alongside the workspace.
   * The dialog closes itself when this fires.
   *
   * Replaces the inline Step 3 ("Check Readiness") that previously
   * lived inside the modal — operators needed to inspect the
   * workspace while resolving findings, and a modal blocked that.
   */
  onContinueToReadiness: (
    schema: Schema,
    mode: MappingMode,
    selectedSourceTables: string[] | undefined,
    selectedTargetTables: string[] | undefined,
  ) => void;
  /** Available workspace tables for optional source table filtering. */
  workspaceTables?: WorkspaceTable[];
}

// ── Component ────────────────────────────────────────────────────────

const GenerateMappingDialog: React.FC<GenerateMappingDialogProps> = ({
  open,
  onClose,
  batchId,
  sessionId,
  currentSchemaId,
  onMappingEngineStart,
  onIterativeStart,
  onContinueToReadiness,
  workspaceTables,
}) => {
  // Two-step dialog now: schema → mode. The readiness gate used to be
  // step 3 inside this modal; it has been promoted to a persistent
  // right-side drawer the host owns so the workspace canvas stays
  // visible / interactive while the operator resolves findings.
  const [step, setStep] = useState<'schema' | 'mode'>('schema');
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [selectedMode, setSelectedMode] = useState<MappingMode>('iterative');
  /** Actual target table names resolved from schema definitions. */
  const [resolvedTargetTables, setResolvedTargetTables] = useState<string[]>([]);

  // Source table filter state
  const [selectedSourceTables, setSelectedSourceTables] = useState<Set<string>>(new Set());
  // Target table filter state
  const [selectedTargetTables, setSelectedTargetTables] = useState<Set<string>>(new Set());

  // Schema list state
  const [schemaSets, setSchemaSets] = useState<Schema[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schemaSearchQuery, setSchemaSearchQuery] = useState('');

  const fetchSchemaSets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listSchemas({ status: 'active' });
      setSchemaSets(result);
    } catch (e: any) {
      setError(e.message || 'Failed to load schema sets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchSchemaSets();
      setStep('schema');
      setSelectedSchema(null);
      setSelectedMode('iterative');
      setResolvedTargetTables([]);
      setSelectedTargetTables(new Set());
      setSchemaSearchQuery('');
      // Pre-select all workspace tables (matches current behavior — all participate)
      if (workspaceTables) {
        setSelectedSourceTables(new Set(workspaceTables.map((t) => t.name)));
      }
    }
  }, [open, fetchSchemaSets, workspaceTables]);

  const filteredSchemaSets = useMemo(() => {
    const q = schemaSearchQuery.trim().toLowerCase();
    if (!q) return schemaSets;
    return schemaSets.filter((schema) => {
      if (schema.schema_name.toLowerCase().includes(q)) return true;
      const desc = schema.metadata?.description;
      return desc ? desc.toLowerCase().includes(q) : false;
    });
  }, [schemaSets, schemaSearchQuery]);

  useEffect(() => {
    setSelectedSchema((prev) => {
      if (!prev) return prev;
      return filteredSchemaSets.some((s) => s.schema_id === prev.schema_id) ? prev : null;
    });
  }, [filteredSchemaSets]);

  const handleSchemaClick = useCallback((schema: Schema) => {
    setSelectedSchema(schema);
  }, []);

  const resolveTargetTablesForSchema = useCallback(async (schema: Schema) => {
    try {
      const tableNames = await resolveTargetTableNames(schema);
      const mappable = getMappableTableNames(schema.definition, tableNames);
      return { tableNames, mappable };
    } catch {
      const fallbackNames = schema.definition?.tables
        ? Object.keys(schema.definition.tables)
        : [];
      return { tableNames: fallbackNames, mappable: fallbackNames };
    }
  }, []);

  const handleBack = useCallback(() => {
    setStep('schema');
  }, []);

  // Compute whether source tables have been filtered (not all selected)
  const allSourceTablesSelected = useMemo(() => {
    if (!workspaceTables) return true;
    return workspaceTables.every((t) => selectedSourceTables.has(t.name));
  }, [workspaceTables, selectedSourceTables]);

  // Compute whether target tables have been filtered
  const allTargetTablesSelected = useMemo(() => {
    return resolvedTargetTables.length === selectedTargetTables.size;
  }, [resolvedTargetTables, selectedTargetTables]);

  const skipModeStep = useMemo(
    () => isTemplateTabUploadedTargetSchema(selectedSchema),
    [selectedSchema],
  );

  const handoffToReadiness = useCallback((
    schema: Schema,
    mode: MappingMode,
    resolvedTargets: string[],
    selectedTargets: string[],
  ) => {
    const sourceTables = allSourceTablesSelected
      ? undefined
      : Array.from(selectedSourceTables);
    const targetTables =
      resolvedTargets.length > 0 && selectedTargets.length === resolvedTargets.length
        ? undefined
        : selectedTargets;
    onContinueToReadiness(schema, mode, sourceTables, targetTables);
    onClose();
  }, [
    allSourceTablesSelected,
    selectedSourceTables,
    onContinueToReadiness,
    onClose,
  ]);

  const handleNext = useCallback(async () => {
    if (!selectedSchema) return;
    const { tableNames, mappable } = await resolveTargetTablesForSchema(selectedSchema);
    setResolvedTargetTables(tableNames);
    setSelectedTargetTables(new Set(mappable));

    if (isTemplateTabUploadedTargetSchema(selectedSchema)) {
      handoffToReadiness(selectedSchema, 'iterative', tableNames, mappable);
      return;
    }
    setStep('mode');
  }, [selectedSchema, resolveTargetTablesForSchema, handoffToReadiness]);

  /**
   * Pre-mapping readiness gate handoff: opens the persistent readiness drawer.
   * Non–Template-tab schemas reach here from step 2; Template-tab uploads skip via handleNext.
   */
  const handleContinueToReadiness = useCallback(() => {
    if (!selectedSchema) return;
    handoffToReadiness(
      selectedSchema,
      selectedMode,
      resolvedTargetTables,
      Array.from(selectedTargetTables),
    );
  }, [
    selectedSchema,
    selectedMode,
    resolvedTargetTables,
    selectedTargetTables,
    handoffToReadiness,
  ]);

  // Mode step's primary action.
  const handleStart = handleContinueToReadiness;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        {step === 'mode' && (
          <Button
            size="small"
            onClick={handleBack}
            sx={{ minWidth: 0, p: 0.5, mr: 0.5 }}
          >
            <BackIcon fontSize="small" />
          </Button>
        )}
        <SchemaIcon color="primary" />
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          {step === 'schema' ? 'Select Target Schema' : 'Choose Mapping Mode'}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Step {step === 'schema' ? 1 : 2} of {skipModeStep && step === 'schema' ? 1 : 2}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* ── Step 1: Schema Selection ── */}
        {step === 'schema' && (
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loading && !error && schemaSets.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <SchemaIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No schemas available
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Create one in Settings &rarr; Schemas
                </Typography>
              </Box>
            )}

            {!loading && !error && schemaSets.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  size="small"
                  placeholder="Search schemas..."
                  value={schemaSearchQuery}
                  onChange={(e) => setSchemaSearchQuery(e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 32,
                      fontSize: 12,
                      borderRadius: 1.5,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {!loading && !error && schemaSets.length > 0 && filteredSchemaSets.length === 0 && (
              <Box sx={{ py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No schemas match your search
                </Typography>
              </Box>
            )}

            {!loading &&
              filteredSchemaSets.map((schema) => {
                const isCurrent = schema.schema_id === currentSchemaId;
                const isSelected = schema.schema_id === selectedSchema?.schema_id;
                const tableCount = schema.definition?.tables ? Object.keys(schema.definition.tables).length : 0;

                return (
                  <Card
                    key={schema.schema_id}
                    variant="outlined"
                    onClick={() => handleSchemaClick(schema)}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      borderColor: isSelected
                        ? 'primary.main'
                        : isCurrent
                          ? alpha('#3b82f6', 0.4)
                          : 'divider',
                      bgcolor: isSelected
                        ? alpha('#3b82f6', 0.06)
                        : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha('#3b82f6', 0.03),
                      },
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Radio
                          checked={isSelected}
                          size="small"
                          sx={{ p: 0, mr: 0.5 }}
                          tabIndex={-1}
                        />
                        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }}>
                          {schema.schema_name}
                        </Typography>
                        <Chip
                          label={`v${schema.version}`}
                          size="small"
                          sx={{ fontSize: 9, height: 18, bgcolor: alpha('#64748b', 0.1) }}
                        />
                        {tableCount > 0 && (
                          <Chip
                            label={`${tableCount} table${tableCount !== 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: 9, height: 18 }}
                          />
                        )}
                        {isCurrent && (
                          <Chip
                            label="Current"
                            size="small"
                            color="primary"
                            sx={{ fontSize: 9, height: 18 }}
                          />
                        )}
                      </Stack>
                      {schema.metadata?.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            mt: 0.5,
                            display: 'block',
                            fontSize: 11,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            pl: 3.5,
                          }}
                        >
                          {schema.metadata.description}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </>
        )}

        {/* ── Step 2: Mode Selection ── */}
        {step === 'mode' && selectedSchema && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Mapping to <strong>{selectedSchema.schema_name}</strong> ({resolvedTargetTables.length} tables)
            </Typography>

            {/* Iterative (AI-Assisted) option */}
            <Card
              variant="outlined"
              onClick={() => setSelectedMode('iterative')}
              sx={{
                mb: 1.5,
                cursor: 'pointer',
                borderColor: selectedMode === 'iterative' ? 'primary.main' : 'divider',
                bgcolor: selectedMode === 'iterative' ? alpha('#3b82f6', 0.06) : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', bgcolor: alpha('#3b82f6', 0.03) },
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Radio checked={selectedMode === 'iterative'} size="small" sx={{ p: 0 }} tabIndex={-1} />
                  <IterativeIcon sx={{ fontSize: 20, color: selectedMode === 'iterative' ? 'primary.main' : 'text.secondary' }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }}>
                    Iterative (AI-Assisted)
                  </Typography>
                  <Chip
                    label="Recommended"
                    size="small"
                    color="primary"
                    sx={{ fontSize: 9, height: 18 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 4.5 }}>
                  AI generates mappings conversationally, table by table.
                  Faster, great for exploration and iteration.
                </Typography>
              </CardContent>
            </Card>

            <Card
              variant="outlined"
              onClick={() => setSelectedMode('engine')}
              sx={{
                mb: 1,
                cursor: 'pointer',
                borderColor: selectedMode === 'engine' ? 'primary.main' : 'divider',
                bgcolor: selectedMode === 'engine' ? alpha('#3b82f6', 0.06) : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { borderColor: 'primary.main', bgcolor: alpha('#3b82f6', 0.03) },
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Radio checked={selectedMode === 'engine'} size="small" sx={{ p: 0 }} tabIndex={-1} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: 13 }}>
                    Mapping Engine
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 4.5 }}>
                  Production-quality mappings with confidence scoring and the Mapping Editor.
                  Uses the full pipeline with QA checks.
                </Typography>
              </CardContent>
            </Card>

            {/* Target Tables filter (optional) */}
            {resolvedTargetTables.length > 0 && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SchemaIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
                      Target Tables
                    </Typography>
                    <Chip
                      label={allTargetTablesSelected
                        ? `All (${resolvedTargetTables.length})`
                        : `${selectedTargetTables.size}/${resolvedTargetTables.length}`
                      }
                      size="small"
                      color={allTargetTablesSelected ? 'default' : 'primary'}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: 11 }}>
                    Select which target tables to include in mapping. By default, all tables participate.
                  </Typography>
                  <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                    {/* Select All / Deselect All */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                      <Button
                        size="small"
                        sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1, py: 0 }}
                        onClick={() => {
                          const dataOnly = resolvedTargetTables.filter((t) => isDataTable(t, selectedSchema?.definition));
                          setSelectedTargetTables(new Set(dataOnly.length > 0 ? dataOnly : resolvedTargetTables));
                        }}
                        disabled={allTargetTablesSelected}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        sx={{ fontSize: 10, textTransform: 'none', minWidth: 0, px: 1, py: 0 }}
                        onClick={() => setSelectedTargetTables(new Set())}
                        disabled={selectedTargetTables.size === 0}
                      >
                        Deselect All
                      </Button>
                    </Box>
                    {resolvedTargetTables.map((tableName) => {
                      const isData = isDataTable(tableName, selectedSchema?.definition);
                      return (
                        <FormControlLabel
                          key={tableName}
                          control={
                            <Checkbox
                              checked={selectedTargetTables.has(tableName)}
                              onChange={(e) => {
                                setSelectedTargetTables((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) {
                                    next.add(tableName);
                                  } else {
                                    next.delete(tableName);
                                  }
                                  return next;
                                });
                              }}
                              size="small"
                              sx={{ py: 0.25 }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: 12, color: isData ? 'text.primary' : 'text.disabled' }}
                              >
                                {tableName}
                              </Typography>
                              {!isData && (
                                <Tooltip title="Schema metadata table — not typically a mapping target for source data. Check to include anyway." arrow>
                                  <Chip
                                    label="Definition"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: 9, height: 16, color: 'text.disabled', borderColor: 'divider' }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          }
                          sx={{ display: 'flex', ml: 0, mr: 0 }}
                        />
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Source Tables filter (optional) */}
            {workspaceTables && workspaceTables.length > 0 && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '&::before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TablesIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
                      Source Tables
                    </Typography>
                    <Chip
                      label={allSourceTablesSelected
                        ? `All (${workspaceTables.length})`
                        : `${selectedSourceTables.size}/${workspaceTables.length}`
                      }
                      size="small"
                      color={allSourceTablesSelected ? 'default' : 'primary'}
                      sx={{ fontSize: 10, height: 18 }}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: 11 }}>
                    Select which workspace tables to include in mapping. By default, all tables participate.
                  </Typography>
                  <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                    <WorkspaceTablePicker
                      tables={workspaceTables}
                      selectionMode="multi"
                      selectedTables={selectedSourceTables}
                      onSelectionChange={setSelectedSourceTables}
                      columnSelectionEnabled={false}
                      sessionId={sessionId}
                      pageSize={10}
                      compact
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {step === 'schema' && (
          <Button
            variant="contained"
            disabled={!selectedSchema}
            onClick={handleNext}
            sx={{ textTransform: 'none' }}
          >
            {skipModeStep ? 'Continue to Readiness' : 'Next'}
          </Button>
        )}
        {step === 'mode' && (
          <Button
            variant="contained"
            onClick={handleStart}
            sx={{ textTransform: 'none' }}
          >
            Continue to Readiness
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateMappingDialog;
